import { BottomNavigation, BottomNavigationAction, Paper } from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import EventNoteIcon from '@mui/icons-material/EventNote';
import PersonIcon from '@mui/icons-material/Person';
import { useNavigate } from 'react-router-dom';

export type OperatorBottomNavTab = 'reports' | 'new' | 'tasks' | 'profile';

/** Route coperte dai tab della bottom nav (R-NAV1-OP: `OperatorMobileLayout`). Ordine per matching path. */
export const OPERATOR_NAV_TABS: readonly { key: OperatorBottomNavTab; path: string }[] = [
  { key: 'new', path: '/app/reports/new' },
  { key: 'tasks', path: '/app/planning' },
  { key: 'profile', path: '/app/profile' },
  { key: 'reports', path: '/app/reports' },
] as const;

/** Tab attivo in base alla URL corrente (path più specifico prima). */
export function resolveOperatorBottomNavTab(pathname: string): OperatorBottomNavTab {
  if (pathname.startsWith('/app/reports/new')) return 'new';
  if (/\/app\/reports\/[^/]+\/edit(?:\/|$)/.test(pathname)) return 'new';
  const sorted = [...OPERATOR_NAV_TABS].sort((a, b) => b.path.length - a.path.length);
  const hit = sorted.find((t) => pathname === t.path || pathname.startsWith(`${t.path}/`));
  return hit?.key ?? 'reports';
}

const sxRoot = {
  position: 'fixed' as const,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 1100,
  display: { xs: 'flex', md: 'none' },
  borderTop: 1,
  borderColor: 'divider',
};

interface Props {
  active: OperatorBottomNavTab;
}

/**
 * Bottom nav operatore (xs–md: nascosta da md+). Mount globale: `OperatorMobileLayout` (R-NAV1-OP).
 */
export function OperatorBottomNav({ active }: Props) {
  const navigate = useNavigate();

  return (
    <Paper square elevation={8} sx={sxRoot}>
      <BottomNavigation
        value={active}
        showLabels
        sx={{ width: '100%', bgcolor: 'background.paper' }}
      >
        <BottomNavigationAction
          label="Rapportini"
          value="reports"
          icon={<AssignmentIcon />}
          onClick={() => navigate('/app/reports')}
          sx={active === 'reports' ? { color: 'primary.main' } : undefined}
        />
        <BottomNavigationAction
          label="Nuovo"
          value="new"
          icon={<AddCircleIcon sx={{ color: active === 'new' ? 'secondary.main' : undefined }} />}
          onClick={() => navigate('/app/reports/new')}
          sx={{
            bgcolor: active === 'new' ? 'secondary.main' : 'transparent',
            color: active === 'new' ? 'secondary.contrastText' : undefined,
            maxWidth: 120,
            '&.Mui-selected': { color: active === 'new' ? 'secondary.contrastText' : undefined },
          }}
        />
        <BottomNavigationAction
          label="Attività"
          value="tasks"
          icon={<EventNoteIcon />}
          onClick={() => navigate('/app/planning')}
          sx={active === 'tasks' ? { color: 'primary.main' } : undefined}
        />
        <BottomNavigationAction
          label="Profilo"
          value="profile"
          icon={<PersonIcon />}
          onClick={() => navigate('/app/profile')}
          sx={active === 'profile' ? { color: 'primary.main' } : undefined}
        />
      </BottomNavigation>
    </Paper>
  );
}

/** Padding bottom per contenuto sopra la bottom nav fissa (xs). */
export const OPERATOR_BOTTOM_NAV_SX_PADDING = { pb: { xs: 10, md: 0 } };
